import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { topics } from '@/mocks/topics';

const navItems = [
  { label: '首页', path: '/' },
  { label: '专题合集', path: '/topics' },
  { label: '榜单盘点', path: '/rankings' },
  { label: '关于我们', path: '/about' },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/topics?q=${encodeURIComponent(q)}` : '/topics');
    setKeyword('');
    setSearchOpen(false);
  };

  const tickerItems = topics.slice(0, 5).map((t) => t.title.split('：')[0]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <div className="bg-foreground-950 text-background-100 text-[12px]">
        <div className="px-4 md:px-10 h-9 flex items-center justify-between gap-4">
          <span className="hidden sm:flex items-center gap-2 whitespace-nowrap">
            <i className="ri-calendar-line text-accent-400"></i>
            2026 年 9 月 12 日 星期六
          </span>
          <div className="flex-1 overflow-hidden">
            <div className="flex w-max animate-marquee">
              {[...tickerItems, ...tickerItems].map((item, idx) => (
                <span key={idx} className="flex items-center gap-2 px-5 whitespace-nowrap text-background-200">
                  <i className="ri-fire-fill text-primary-400"></i>
                  {item}
                </span>
              ))}
            </div>
          </div>
          <a href="mailto:hello@heiliaoshe.example.com" className="hidden md:flex items-center gap-2 whitespace-nowrap text-background-200 hover:text-accent-400 transition-colors">
            <i className="ri-mail-line"></i>
            hello@heiliaoshe.example.com
          </a>
        </div>
      </div>

      <div
        className={`border-b transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur border-background-200'
            : 'bg-background-50/80 backdrop-blur border-transparent'
        }`}
      >
        <div className="px-4 md:px-10 h-16 md:h-[72px] flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer">
            <span className="w-9 h-9 md:w-10 md:h-10 rounded-md bg-primary-500 text-background-50 flex items-center justify-center font-heading text-lg md:text-xl font-black">
              黑
            </span>
            <span className="leading-none">
              <span className="block font-heading text-xl md:text-2xl font-black text-foreground-950 tracking-tight">
                黑料社<span className="text-primary-500">网</span>
              </span>
              <span className="hidden sm:block text-[11px] tracking-[0.3em] text-foreground-500 mt-1">
                专题合集 · 精选盘点
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="主导航">
            {navItems.map((item) => {
              const active = item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                    active
                      ? 'text-primary-600 bg-primary-50 font-medium'
                      : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            {searchOpen && (
              <form onSubmit={submitSearch} className="hidden md:flex items-center gap-2 bg-background-100 border border-background-300 rounded-md px-3 h-10">
                <i className="ri-search-line text-foreground-500 text-base"></i>
                <input
                  autoFocus
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索专题或话题"
                  className="bg-transparent text-sm outline-none w-36 lg:w-44 text-foreground-900 placeholder:text-foreground-400"
                />
              </form>
            )}
            <button
              type="button"
              aria-label="搜索"
              onClick={() => setSearchOpen((v) => !v)}
              className="w-10 h-10 rounded-md border border-background-300 text-foreground-700 hover:text-primary-600 hover:border-primary-300 flex items-center justify-center transition-colors cursor-pointer"
            >
              <i className={searchOpen ? 'ri-close-line text-lg' : 'ri-search-line text-lg'}></i>
            </button>
            <Link
              to="/topics"
              className="hidden md:inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              开始浏览
              <i className="ri-arrow-right-line"></i>
            </Link>
            <button
              type="button"
              aria-label="菜单"
              onClick={() => setMenuOpen((v) => !v)}
              className="lg:hidden w-10 h-10 rounded-md border border-background-300 text-foreground-800 flex items-center justify-center cursor-pointer"
            >
              <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-2-line text-xl'}></i>
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-3">
            <nav className="flex flex-col" aria-label="移动端导航">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="py-3 px-2 text-sm text-foreground-800 border-b border-background-200 last:border-0 cursor-pointer flex items-center justify-between"
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-foreground-400"></i>
                </Link>
              ))}
            </nav>
            <form onSubmit={submitSearch} className="mt-3 flex items-center gap-2 bg-background-100 border border-background-300 rounded-md px-3 h-11">
              <i className="ri-search-line text-foreground-500"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索专题或话题"
                className="bg-transparent text-sm outline-none flex-1 text-foreground-900 placeholder:text-foreground-400"
              />
            </form>
          </div>
        )}
      </div>
    </header>
  );
}